/**
 * Inbox deep-link query helpers — keep in sync with
 * store/cable.js openInitialRouteFromUrl / applyInboxUrlFromLocation.
 *
 * Supported params:
 *   mailbox_id, folder (CSR key: inbox|starred|folder_N|tag_N|…),
 *   thread_id, view (rules|thread), compose (new|reply|reply_all|forward)
 */

const COMPOSE_TYPES = new Set(['new', 'reply', 'reply_all', 'forward'])
const VIEW_TYPES = new Set(['rules', 'thread'])

/**
 * @param {{
 *   mailboxId?: number|string|null,
 *   folder?: string|null,
 *   threadId?: number|string|null,
 *   view?: string|null,
 *   compose?: string|null,
 * }} state
 * @returns {URLSearchParams}
 */
export function buildInboxSearchParams(state = {}) {
  const params = new URLSearchParams()
  const mailboxId = state.mailboxId
  if (mailboxId != null && mailboxId !== '') {
    params.set('mailbox_id', String(mailboxId))
  }

  const folder = state.folder
  if (folder && folder !== 'inbox') {
    params.set('folder', String(folder))
  }

  const threadId = state.threadId
  if (threadId != null && threadId !== '') {
    params.set('thread_id', String(threadId))
  }

  if (VIEW_TYPES.has(state.view)) {
    params.set('view', state.view)
  }

  if (COMPOSE_TYPES.has(state.compose)) {
    params.set('compose', state.compose)
  }

  return params
}

/**
 * @param {URLSearchParams|string} search
 * @returns {{
 *   mailboxId: number|null,
 *   folder: string,
 *   threadId: number|null,
 *   view: 'rules'|'thread'|null,
 *   compose: 'new'|'reply'|'reply_all'|'forward'|null,
 * }}
 */
export function parseInboxSearchParams(search) {
  const params = typeof search === 'string'
    ? new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
    : search

  const mailboxRaw = params.get('mailbox_id')
  const threadRaw = params.get('thread_id')
  const folderRaw = params.get('folder')
  const viewRaw = params.get('view')
  const composeRaw = params.get('compose')

  let mailboxId = null
  if (mailboxRaw != null && mailboxRaw !== '') {
    const n = Number(mailboxRaw)
    mailboxId = Number.isFinite(n) ? n : null
  }

  let threadId = null
  if (threadRaw != null && threadRaw !== '') {
    const n = Number(threadRaw)
    threadId = Number.isFinite(n) && !Number.isNaN(n) ? n : null
  }

  return {
    mailboxId,
    folder: folderRaw && folderRaw !== '' ? folderRaw : 'inbox',
    threadId,
    view: VIEW_TYPES.has(viewRaw) ? viewRaw : null,
    compose: COMPOSE_TYPES.has(composeRaw) ? composeRaw : null,
  }
}

/**
 * @param {object} state
 * @param {string} [pathname='/emails/inbox']
 * @returns {string}
 */
export function inboxUrlFromState(state, pathname = '/emails/inbox') {
  const qs = buildInboxSearchParams(state).toString()
  return qs ? `${pathname}?${qs}` : pathname
}
